/** MicShapes — shout. Line weight follows microphone volume. */
import type { CreateModule } from '../../core/module-api';
import { cfgNum } from '../../core/module-api';
import type { Point } from '../../core/scene';
import { defaultStyle } from '../../core/scene';
import { distance, strokeOutline } from '../../core/geometry';

const ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0"/><path d="M12 18v3"/></svg>';

export function createMic(): CreateModule {
  let pts: Point[] = [];
  let vols: number[] = [];
  let drawing = false;

  return {
    manifest: {
      id: 'mic',
      kind: 'create',
      name: 'Mic',
      apiVersion: 1,
      description: 'Shout at the canvas — your voice sets the line weight.',
      icon: ICON,
      capabilities: ['audio'],
      params: [
        { kind: 'slider', key: 'min', label: 'Min', min: 0.5, max: 20, step: 0.5, default: 1 },
        { kind: 'slider', key: 'max', label: 'Max', min: 4, max: 90, step: 1, default: 48 },
      ],
    },

    onPointerDown(s, ctx) {
      pts = [{ x: s.x, y: s.y }];
      vols = [ctx.audio?.volume ?? 0];
      drawing = true;
    },

    onPointerMove(s, ctx) {
      if (!drawing) return;
      const p = { x: s.x, y: s.y };
      const last = pts[pts.length - 1];
      if (!last || distance(last, p) >= 1.2) {
        pts.push(p);
        vols.push(ctx.audio?.volume ?? 0);
      }
    },

    onPointerUp() {
      drawing = false;
    },

    getLiveShapes(ctx) {
      if (pts.length === 0) return [];
      const min = cfgNum(ctx.config, 'min', 1);
      const max = cfgNum(ctx.config, 'max', 48);
      const widths = vols.map((v) => min + Math.max(0, Math.min(1, v)) * (max - min));
      const style = defaultStyle();
      style.mode = 'fill';
      style.color = { ...ctx.ink };
      const shape = ctx.newShape({ path: strokeOutline(pts, widths), style });
      shape.variableWidth = { spine: pts.map((p) => ({ ...p })), width: widths };
      return [shape];
    },

    onClear() {
      pts = [];
      vols = [];
    },

    onCommit() {
      pts = [];
      vols = [];
    },
  };
}
